import { useContext, useEffect, useId, useState } from 'react'

import { Accordion } from '@awazevr/design-system.components.accordion'
import { useForm } from '@awazevr/design-system.components.form'
import { useIntlContext } from '@awazevr/mfe-common.contexts.intl'
import {
  useSearchContextDispatch,
  useSearchContextState,
} from '@awazevr/mfe-common.contexts.search'
import { TmsContext } from '@awazevr/mfe-common.contexts.tms'
import { AggregationFeatures } from '@awazevr/mfe-common.types-and-mocks.one-search-client'

import { OPTIMIZELY_FLAGS_ENUM } from '@/constants'
import { useOptimizelyDecisionsContext } from '@/context/optimizely-decisions-context'
import { useApplicationConfig } from '@/lib/applicationConfig'
import { useDisplayContext } from '@/lib/display/displayContext'
import { getFilterTypeArray } from '@/utils/helpers'

import { handlePriceFilterChange } from './FilterActions'
import {
  AllFeaturesAccordion,
  BathroomsAccordion,
  BedroomsAccordion,
  CustomerReviewAccordion,
  ExtrasAccordion,
  getOrderedAccordionSections,
  GroupedFilterAccordion,
  PopularDestinationsAccordion,
  PopularFeaturesAccordion,
  PriceRangeAccordion,
  StarRatingAccordion,
} from './Filters.accordions'
import { FilterAccordionOrderByBrand } from './Filters.mappings'
import { PriceFilterFormValues } from './Filters.types'
import {
  buildBathroomArray,
  buildBedroomArray,
  buildGradesArray,
  buildReviewsArray,
  buildSelectedFilters,
  containsYear2025,
  containsYear2027,
  getAllFeatures,
  getPopularDestinations,
  getPopularFeatures,
  groupFiltersByCategory,
  processFeaturesAndSiteFeatures,
} from './Filters.utils'
import { SelectedFilters } from './SelectedFilters'

import styles from './Filters.module.scss'

export const Filters: React.FC = () => {
  const filterGroupIdSuffix = useId()
  const optiContext = useOptimizelyDecisionsContext()
  const { currencySymbol, formatMessage } = useIntlContext()
  const dispatch = useSearchContextDispatch()
  const { brand } = useApplicationConfig()
  const formHooks = useForm<PriceFilterFormValues>()
  const priceFilterObserver = formHooks.watch
  const [priceRangeError, setPriceRangeError] = useState<boolean>(false)

  const {
    aggregations = null,
    searchCriteria = {},
    hasResults,
    totalSelectedFilters = 0,
    loading,
  } = useSearchContextState()

  const {
    accommodationTypes: selectedAccommodationTypeIds = [],
    features: selectedFeatureIds = [],
    siteFeatures: selectedSiteFeatureIds = [],
    bathrooms = '',
    bedrooms = '',
    grade = '',
    rating = '',
    minPrice,
    maxPrice,
    start = '',
  } = searchCriteria

  const { displayMode } = useDisplayContext()

  const selectedFiltersFromSearchCriteria = [
    ...selectedAccommodationTypeIds,
    ...selectedFeatureIds,
    ...selectedSiteFeatureIds,
  ]

  const aggregationSiteFeatures = aggregations?.siteFeatures ?? []
  const aggregationsFeatures = aggregations?.features ?? []
  const aggregationsAccommodationTypes = aggregations?.accommodationTypes ?? []
  const accommodationTypesAsFeatures = aggregationsAccommodationTypes.map(
    ({ accommodationId, count }) => ({
      featureCode: accommodationId,
      featureId: accommodationId,
      featureName: formatMessage({
        id: `filters.accommodation-types.${accommodationId}`,
        defaultMessage: accommodationId,
      }),
      count,
    }),
  )

  const groupedFiltersVariant = optiContext?.getExperimentVariationKey(
    OPTIMIZELY_FLAGS_ENUM.FILTER_CATEGORIES,
  )

  const enableGroupedFilters =
    groupedFiltersVariant === 'variant_a' ||
    groupedFiltersVariant === 'variant_b'
  const isGroupedFiltersVariantB = groupedFiltersVariant === 'variant_b'
  const filterAccordionOrder = FilterAccordionOrderByBrand[brand] ?? []

  // HACK: GD_128_ONESEARCH_V2 This needs to be removed once OneSearch V2 supports dated features. Ask Blake or Liam for more details
  const hideFreeCancellation =
    containsYear2027(start) || containsYear2025(start)

  const combinedAndSortedFeatures = processFeaturesAndSiteFeatures(
    [...aggregationsFeatures, ...accommodationTypesAsFeatures],
    aggregationSiteFeatures,
    hideFreeCancellation,
  )

  const aggregationOfPopularFeatures = aggregations?.popularFeatures ?? []

  const versionedPopularFeatureIds = aggregationOfPopularFeatures.map(
    (feature: AggregationFeatures) => {
      return feature.featureId
    },
  )

  const popularFeatures = getPopularFeatures(
    combinedAndSortedFeatures,
    versionedPopularFeatureIds,
  )

  const popularDestinations = getPopularDestinations(combinedAndSortedFeatures)
  const popularDestinationIds = popularDestinations.map(({ featureId }) =>
    String(featureId),
  )
  const allVisibleFeatures = getAllFeatures(combinedAndSortedFeatures)

  const bathroomOptions = buildBathroomArray({
    formatMessage,
    rooms: aggregations?.bathrooms,
  })

  const reviewOptions = buildReviewsArray(aggregations?.reviews)

  const bedroomOptions = buildBedroomArray({
    formatMessage,
    rooms: aggregations?.bedrooms,
  })

  const gradeOptions = buildGradesArray({
    grades: aggregations?.grades || [],
    formatMessage,
  })

  const displayGradeOptions = buildGradesArray({
    grades: aggregations?.grades || [],
    display: true,
    formatMessage,
  })

  const selectedFeatures = buildSelectedFilters(
    combinedAndSortedFeatures,
    selectedFiltersFromSearchCriteria,
  )

  const selectedBathrooms = getFilterTypeArray(bathrooms)

  const selectedBedrooms = getFilterTypeArray(bedrooms)

  const selectedGrades = getFilterTypeArray(grade)

  const selectedRatings = getFilterTypeArray(rating)

  const tms = useContext(TmsContext)
  const isLoading = Boolean(loading)

  const isUpdating = combinedAndSortedFeatures.length > 0 && isLoading

  const showPriceFilter = start

  const shouldShowZeroFeaturesCount =
    !hasResults && !loading && displayMode === 'MAP'

  useEffect(() => {
    formHooks.setValue('minPrice', minPrice)
    formHooks.setValue('maxPrice', maxPrice)
  }, [minPrice, maxPrice])

  useEffect(() => {
    const subscription = priceFilterObserver((values) =>
      handlePriceFilterChange(values, setPriceRangeError),
    )
    return () => subscription.unsubscribe()
  }, [priceFilterObserver])

  const isInAlphabeticalFiltersExp =
    optiContext?.getExperimentVariationKey(
      OPTIMIZELY_FLAGS_ENUM.ALPHABETICALLY_ORDERED_FILTERS,
    ) === 'variant_a'

  const showPopularDestinationFiltersExp =
    optiContext?.getExperimentVariationKey(
      OPTIMIZELY_FLAGS_ENUM.REMOVE_POPULAR_DESTINATION_FILTER,
    ) !== 'variant_a'

  const groupedFilters = enableGroupedFilters
    ? groupFiltersByCategory({
        features: [...aggregationsFeatures, ...aggregationSiteFeatures],
        brand,
      }).map((group) => ({
        ...group,
        groupName: formatMessage({
          id: group.translationKey,
          defaultMessage: group.groupName,
        }),
      }))
    : []

  const accordionSections = [
    {
      key: 'popularFeatures',
      title: formatMessage({
        id: 'filters.popular-features.heading',
        defaultMessage: 'Popular features',
      }),
      element: (
        <PopularFeaturesAccordion
          accommodationTypesAsFeatures={accommodationTypesAsFeatures}
          allFeatures={combinedAndSortedFeatures}
          dispatch={dispatch}
          filterGroupIdSuffix={filterGroupIdSuffix}
          formatMessage={formatMessage}
          isInAlphabeticalFiltersExp={isInAlphabeticalFiltersExp}
          loading={isLoading}
          optiContext={optiContext}
          popularFeatures={popularFeatures}
          selectedFilters={selectedFiltersFromSearchCriteria}
          shouldShowZeroFeaturesCount={shouldShowZeroFeaturesCount}
          siteFeatures={aggregationSiteFeatures}
          tms={tms}
        />
      ),
    },
    {
      key: 'popularDestinations',
      title: formatMessage({
        id: 'filters.popular-destinations',
        defaultMessage: 'Popular destinations',
      }),
      element: (
        <PopularDestinationsAccordion
          accommodationTypesAsFeatures={accommodationTypesAsFeatures}
          allFeatures={combinedAndSortedFeatures}
          brand={brand}
          dispatch={dispatch}
          filterGroupIdSuffix={filterGroupIdSuffix}
          formatMessage={formatMessage}
          isInAlphabeticalFiltersExp={isInAlphabeticalFiltersExp}
          loading={isLoading}
          optiContext={optiContext}
          popularDestinations={popularDestinations}
          selectedFilters={selectedFiltersFromSearchCriteria}
          shouldShowZeroFeaturesCount={shouldShowZeroFeaturesCount}
          showPopularDestinationFiltersExp={showPopularDestinationFiltersExp}
          siteFeatures={aggregationSiteFeatures}
          tms={tms}
        />
      ),
    },
    {
      key: 'allFeatures',
      title: formatMessage({
        id: 'filters.all-features.heading',
        defaultMessage: 'All features',
      }),
      element: enableGroupedFilters ? null : (
        <AllFeaturesAccordion
          accommodationTypesAsFeatures={accommodationTypesAsFeatures}
          allFeatures={combinedAndSortedFeatures}
          allVisibleFeatures={allVisibleFeatures}
          dispatch={dispatch}
          filterGroupIdSuffix={filterGroupIdSuffix}
          formatMessage={formatMessage}
          isInAlphabeticalFiltersExp={isInAlphabeticalFiltersExp}
          loading={isLoading}
          optiContext={optiContext}
          selectedFilters={selectedFiltersFromSearchCriteria}
          shouldShowZeroFeaturesCount={shouldShowZeroFeaturesCount}
          siteFeatures={aggregationSiteFeatures}
          tms={tms}
        />
      ),
    },
    ...groupedFilters.map((group) => ({
      key: group.key,
      title: group.groupName,
      element: (
        <GroupedFilterAccordion
          key={group.key}
          accommodationTypesAsFeatures={accommodationTypesAsFeatures}
          allFeatures={combinedAndSortedFeatures}
          dispatch={dispatch}
          filterGroupIdSuffix={filterGroupIdSuffix}
          formatMessage={formatMessage}
          group={group}
          optiContext={optiContext}
          selectedFilters={selectedFiltersFromSearchCriteria}
          siteFeatures={aggregationSiteFeatures}
          tms={tms}
        />
      ),
    })),
    {
      key: 'price',
      title: formatMessage(
        {
          id: 'filters.price-range.heading',
          defaultMessage: 'Price Range ({currency})',
        },
        { currency: currencySymbol },
      ),
      element: (
        <PriceRangeAccordion
          currencySymbol={currencySymbol}
          dispatch={dispatch}
          formatMessage={formatMessage}
          formHooks={formHooks}
          priceRangeError={priceRangeError}
          setPriceRangeError={setPriceRangeError}
          showPriceFilter={showPriceFilter}
          tms={tms}
        />
      ),
    },
    {
      key: 'bedrooms',
      title: formatMessage({
        id: 'filters.bedrooms.heading',
        defaultMessage: 'Bedrooms',
      }),
      element: (
        <BedroomsAccordion
          bedroomOptions={bedroomOptions}
          dispatch={dispatch}
          filterGroupIdSuffix={filterGroupIdSuffix}
          formatMessage={formatMessage}
          loading={isLoading}
          optiContext={optiContext}
          selectedBedrooms={selectedBedrooms}
          shouldShowZeroFeaturesCount={shouldShowZeroFeaturesCount}
          tms={tms}
        />
      ),
    },
    {
      key: 'bathrooms',
      title: formatMessage({
        id: 'filters.bathrooms.heading',
        defaultMessage: 'Bathrooms',
      }),
      element: (
        <BathroomsAccordion
          bathroomOptions={bathroomOptions}
          dispatch={dispatch}
          filterGroupIdSuffix={filterGroupIdSuffix}
          formatMessage={formatMessage}
          loading={isLoading}
          optiContext={optiContext}
          selectedBathrooms={selectedBathrooms}
          shouldShowZeroFeaturesCount={shouldShowZeroFeaturesCount}
          tms={tms}
        />
      ),
    },
    {
      key: 'starRating',
      title: formatMessage({
        id: 'filters.star-rating.heading',
        defaultMessage: 'Star rating',
      }),
      element: (
        <StarRatingAccordion
          dispatch={dispatch}
          displayGradeOptions={displayGradeOptions}
          filterGroupIdSuffix={filterGroupIdSuffix}
          formatMessage={formatMessage}
          gradeOptions={gradeOptions}
          loading={isLoading}
          optiContext={optiContext}
          selectedGrades={selectedGrades}
          shouldShowZeroFeaturesCount={shouldShowZeroFeaturesCount}
          tms={tms}
        />
      ),
    },
    {
      key: 'customerReviewScore',
      title: formatMessage({
        id: 'filters.customer-review.heading',
        defaultMessage: 'Customer review score',
      }),
      element: (
        <CustomerReviewAccordion
          dispatch={dispatch}
          filterGroupIdSuffix={filterGroupIdSuffix}
          formatMessage={formatMessage}
          loading={isLoading}
          optiContext={optiContext}
          reviewOptions={reviewOptions}
          selectedRatings={selectedRatings}
          shouldShowZeroFeaturesCount={shouldShowZeroFeaturesCount}
          tms={tms}
        />
      ),
    },
    {
      key: 'extras',
      title: formatMessage({
        id: 'filters.extras.heading',
        defaultMessage: 'Extras',
      }),
      element: (
        <ExtrasAccordion
          discountsSelected={Boolean(searchCriteria.discounts)}
          dispatch={dispatch}
          filterGroupIdSuffix={filterGroupIdSuffix}
          formatMessage={formatMessage}
          optiContext={optiContext}
          showExtras={start}
          tms={tms}
        />
      ),
    },
  ]

  const orderedAccordionSections = getOrderedAccordionSections(
    accordionSections,
    {
      orderedKeys: isGroupedFiltersVariantB ? filterAccordionOrder : [],
    },
  )

  return (
    <div
      className={`${styles.container} ${isUpdating ? styles.isUpdating : ''}`}
    >
      <Accordion>
        <SelectedFilters
          accommodationTypesAsFeatures={accommodationTypesAsFeatures}
          popularDestinationIds={popularDestinationIds}
          popularFeatureIds={versionedPopularFeatureIds}
          searchCriteria={searchCriteria}
          selectedFilters={selectedFeatures}
          siteFeatures={aggregationSiteFeatures}
          totalFiltersSelectedCount={totalSelectedFilters}
          dispatch={dispatch}
        />
        {orderedAccordionSections}
      </Accordion>
    </div>
  )
}
